
import { useI18n } from "../../shared/i18n";
import type { Keys } from "../../shared/i18n";

/** One section: [label, view key, write key | null, keys that follow the view box].
 *
 *  FOLLOWERS are keys the grid never draws and never grants, but which IMPLY the view key.
 *  Left out of the manifest they stay with the role, and the implication hands the view
 *  back after every save that removed it -- the box cannot be unticked, and the editor
 *  says Saved and Unsaved at once. So they go with the view box: unticking it takes them
 *  too, and ticking it again puts back only what the role gives. */
type Row = readonly [
  label: string,
  view: string,
  write: string | null,
  follows?: readonly string[],
];

/** A system, the key that opens it at all, and its sections.
 *
 *  `entry` is the permission the GATEWAY checks before a request ever reaches the module.
 *  Without it nothing else can take effect, which is why the screen puts it first and
 *  disables everything below when it is off.
 *
 *  Each section is a Row. Written out rather than derived from the key strings: the ORDER
 *  is the module's own sidebar order, which no sort of `module:action` reproduces, and
 *  seven sections have NO write route — they must offer Deny and View only, rather than a
 *  Write nothing on the other side reads.
 *
 *  This is also the SAVE MANIFEST. The payload is built from these keys and nothing else,
 *  so a permission the screen never drew can never be granted, revoked or cleared by it.
 *  `analysis:admin` is deliberately absent: it is a LEVEL rather than a section, and
 *  including it here would mean every save silently cleared it.
 *
 *  `help` is drawn under the system while it is on. `shopScope` names the key that gives
 *  a person every shop, and the roles whose shop comes from the account itself; any other
 *  role with a page but without that key gets a warning, because the module will open
 *  the page and have no shop to show in it. */
export const SYSTEMS: {
  module: string;
  title: string;
  entry: string;
  note: string;
  help?: Keys;
  shopScope?: { key: string; ownShop: readonly string[]; warning: Keys };
  rows: readonly Row[];
}[] = [
  {
    module: "analysis",
    title: "Analysis",
    entry: "analysis:read",
    note: "Sales, profitability, inventory and the data behind them.",
    rows: [
      ["Data Intake", "analysis:intake_read", "analysis:intake_write"],
      ["Data Master", "analysis:master_read", "analysis:master_write"],
      ["Dashboard Home", "analysis:home_read", null],
      ["Detailed Analysis", "analysis:detailed_read", "analysis:detailed_write"],
      ["Sales", "analysis:sales_read", null],
      ["Profitability", "analysis:profit_read", "analysis:finance_write"],
      ["Inventory", "analysis:inventory_read", null],
      ["Supply Chain", "analysis:supply_read", "analysis:supply_write"],
      ["Customers", "analysis:customers_read", null],
      ["Alerts", "analysis:alerts_read", "analysis:alerts_write"],
      ["Recommendations", "analysis:recommendations_read", "analysis:recommendations_write"],
      ["Configuration", "analysis:configuration_read", "analysis:configuration_write"],
      ["Data Quality", "analysis:quality_read", "analysis:quality_write"],
      ["Users", "analysis:users_read", "analysis:users_write"],
      ["Saved Views", "analysis:savedviews_read", null],
      ["Audit Log", "analysis:audit_read", null],
      ["Customer contact details", "analysis:customer_pii", null],
    ],
  },
  {
    // Reconciliation is one page, so its "sections" are its three capabilities.
    // `accounting:approve` is a FEATURE — sign-off is what the Accountant and Reviewer
    // roles are for — so it is neither drawn nor touched here.
    module: "accounting",
    title: "Reconciliation",
    entry: "accounting:read",
    note: "Matching payouts against the ledger.",
    rows: [
      // The entry key is also this row's View level, and that is the honest shape: to
      // open Reconciliation IS to be able to read it. Turning the system on therefore
      // shows this row as View only, and Write adds the ability to post a decision.
      ["Reconciliation", "accounting:read", "accounting:write"],
      ["Download the report", "accounting:export", null],
    ],
  },
  {
    // The `grooming` module, which the sidebar has called Sales since shell migration
    // 0019: one row per page in its rail. Every grooming key is in this manifest, so
    // switching the system off leaves the person nothing the gateway would let through.
    //
    // The commission work follows Reports. Each of calculate, approve, export and
    // record_payment IMPLIES Reports, so left with the role they held the page open
    // after it was unticked; sync is here so that switching Sales off takes it too.
    //
    // The two Write boxes are honoured more narrowly than they look, and `help` says so
    // on the screen: each works for a shop account at its own shop, and together with
    // All shops for anyone, at the shop picked on the page (shell 0020).
    module: "grooming",
    title: "Sales",
    entry: "grooming:read",
    note: "The shops' daily form, the day's grooming services and commissions.",
    help: "users.salesHelp",
    shopScope: {
      key: "grooming:read_all",
      // The shop account's shop is its email's; a groomer's is their roster row's.
      ownShop: ["shop", "groomer"],
      warning: "users.salesNoShop",
    },
    rows: [
      ["Overview", "grooming:overview_read", null],
      ["Daily Form", "grooming:daily_read", "grooming:activity_write"],
      ["Grooming", "grooming:grooming_read", "grooming:assign"],
      [
        "Reports",
        "grooming:reports_read",
        null,
        [
          "grooming:calculate",
          "grooming:approve",
          "grooming:export",
          "grooming:record_payment",
          "grooming:sync",
        ],
      ],
      ["All shops (not only their own)", "grooming:read_all", null],
    ],
  },
];

/** TWO BOXES PER SECTION: Read and Write.
 *
 *  Read on its own is view-only. Write is shown with Read, because the server closes Write
 *  over Read and a screen that let somebody save one without the other would be drawing a
 *  state that cannot exist. Neither box ticked is no access to that section.
 *
 *  The third state the permission model has -- an EXPLICIT revoke, which reads the same as
 *  "not given by their role" in a checkbox and behaves differently the moment the role
 *  changes -- is not drawn. It does not need to be: unticking a box the role grants still
 *  sends a revoke, because `save` diffs against the role's own closure rather than against
 *  what is on screen. What is lost is only the ability to SEE which of the two it was.
 */
/** Every key this system's rows can express. The SAVE MANIFEST, exported because the
 *  editor now builds the payload: a permission this screen never drew can then never be
 *  granted, revoked or cleared by it. */
export function manifest(system: (typeof SYSTEMS)[number]): string[] {
  const keys = new Set<string>([system.entry]);
  for (const [, view, write, follows = []] of system.rows) {
    keys.add(view);
    if (write) keys.add(write);
    for (const key of follows) keys.add(key);
  }
  return [...keys];
}

/** The keys that only FOLLOW a view box. A save may revoke them, never grant them: they
 *  come from the role and nowhere else. The ticks are seeded from the person's current
 *  access, so without this a role change saved together with a grid edit wrote the OLD
 *  role's followers -- a demoted manager's calculate, approve and export -- as per-user
 *  grants that no screen draws and that outlive every later role change. */
export function followers(system: (typeof SYSTEMS)[number]): Set<string> {
  return new Set(system.rows.flatMap(([, , , follows = []]) => follows));
}

/** The keys a person can see and tick for this system: the manifest less its followers.
 *  What "unsaved" and "saved differently" are measured on -- a follower is derived from
 *  a view box and the role, never edited on its own. */
export function drawn(system: (typeof SYSTEMS)[number]): string[] {
  const hidden = followers(system);
  return manifest(system).filter((key) => !hidden.has(key));
}

/** Which systems a person reaches, and what they may do in each section.
 *
 *  CONTROLLED, and that is the fix for a data-loss bug rather than a preference. This
 *  component used to own `held` and reset it from `access` on every change of that prop.
 *  Saving the identity form refetched access, which replaced `held` -- so every tick an
 *  administrator had made and not yet saved was silently discarded, and the screen then
 *  showed a green "Saved". One owner, in the editor, and one Save for the screen. */
export function AccessGrid({
  held,
  onChange,
  administers,
  role,
  fromRole,
}: {
  held: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Drawn as a warning: the level carries every section and a cleared box will not take
      it away. */
  administers: boolean;
  /** The role chosen in the editor, for `shopScope`. */
  role: string;
  /** What that role gives, closed over the implications: where a section's followers
      come back from when its box is ticked again. */
  fromRole: Set<string>;
}) {
  const { t } = useI18n();

  const setHeld = (update: (prev: Set<string>) => Set<string>) => onChange(update(held));

  const canRead = (view: string) => held.has(view);
  const canWrite = (write: string | null) => write !== null && held.has(write);

  /** Opening a section puts back the followers the ROLE gives, never more: the grid
   *  grants none of them, so ticking Reports for somebody whose role does not calculate
   *  is the page to read and nothing else. */
  function openSection(next: Set<string>, [, view, , follows = []]: Row) {
    if (next.has(view)) return;
    next.add(view);
    for (const key of follows) if (fromRole.has(key)) next.add(key);
  }

  /** Read off also clears Write and the followers: there is no working in a section you
   *  cannot open, and the server would close over it anyway. */
  function setRead(row: Row, on: boolean) {
    const [, view, write, follows = []] = row;
    setHeld((prev) => {
      const next = new Set(prev);
      if (on) openSection(next, row);
      else {
        next.delete(view);
        if (write) next.delete(write);
        for (const key of follows) next.delete(key);
      }
      return next;
    });
  }

  /** Write on also sets Read, for the same reason. */
  function setWrite(row: Row, write: string, on: boolean) {
    setHeld((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(write);
        openSection(next, row);
      } else next.delete(write);
      return next;
    });
  }

  /** Whether this person reaches the system at all. Off means off: every section key
   *  IMPLIES the entry key, so leaving one ticked would hand the entry back on the next
   *  save and the switch would appear not to work. */
  function reaches(system: (typeof SYSTEMS)[number]): boolean {
    return manifest(system).some((key) => held.has(key));
  }

  function setReaches(system: (typeof SYSTEMS)[number], on: boolean) {
    const keys = manifest(system);
    setHeld((prev) => {
      const next = new Set(prev);
      for (const key of keys) next.delete(key);
      if (on) next.add(system.entry);
      return next;
    });
  }

  return (
    <div className="access-grid">
      {administers && (
        <p className="muted small access-grid-note">{t("users.administersAnalysis")}</p>
      )}

      {SYSTEMS.map((system) => {
        const open = reaches(system);
        const granted = system.rows.filter(([, v, w]) => canRead(v) || canWrite(w)).length;
        // A page ticked, no every-shop key, and a role with no shop of its own: the
        // module opens the page and then has no shop to put in it.
        const scope = system.shopScope;
        const noShop =
          open &&
          scope !== undefined &&
          !held.has(scope.key) &&
          !scope.ownShop.includes(role) &&
          system.rows.some(([, view]) => view !== scope.key && held.has(view));
        return (
          <section key={system.module} className="access-system" data-open={open}>
            <table className="access-table">
              <thead>
                <tr>
                  <th scope="col">{t("users.colSection")}</th>
                  <th scope="col" className="access-col">{t("users.colRead")}</th>
                  <th scope="col" className="access-col">{t("users.colWrite")}</th>
                </tr>
              </thead>
              <tbody>
                {/* THE SYSTEM ITSELF, first. Off means off: every section key implies the
                    entry key, so leaving one ticked would hand the entry back on the next
                    save and the switch would look broken. */}
                <tr className="access-system-row">
                  <th scope="row">
                    <strong>{system.title}</strong>
                    <small className="muted">{system.note}</small>
                  </th>
                  <td className="access-col">
                    <input
                      type="checkbox"
                      checked={open}
                      aria-label={system.title}
                      onChange={(e) => setReaches(system, e.target.checked)}
                    />
                  </td>
                  <td className="access-col" />
                </tr>

                {/* Collapsed while the system is off -- there is nothing to choose until
                    somebody can reach it, and a list of dead boxes invites the reader to
                    tick one and wonder why it did nothing. */}
                {open &&
                  system.rows.map((row) => {
                    const [label, view, write] = row;
                    return (
                      <tr key={view}>
                        <th scope="row">{label}</th>
                        <td className="access-col">
                          <input
                            type="checkbox"
                            checked={canRead(view)}
                            aria-label={`${label} — ${t("users.colRead")}`}
                            onChange={(e) => setRead(row, e.target.checked)}
                          />
                        </td>
                        <td className="access-col">
                          {write ? (
                            <input
                              type="checkbox"
                              checked={canWrite(write)}
                              aria-label={`${label} — ${t("users.colWrite")}`}
                              onChange={(e) => setWrite(row, write, e.target.checked)}
                            />
                          ) : (
                            // No mutating route behind this section, so no box to tick. A
                            // Write here would be a promise the server cannot keep.
                            <span className="muted access-none" aria-hidden="true">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {/* What the boxes do not say by themselves. Visible, because the only other
                place it was written down was a code comment, and an administrator who
                ticks Write for somebody the module then refuses sees a Saved and a
                person who still cannot do the thing. */}
            {open && system.help && (
              <p className="muted small access-system-help">{t(system.help)}</p>
            )}
            {/* A live region that is always there, so a warning appearing on a tick is
                announced -- a region inserted together with its text often is not. */}
            {scope && (
              <div role="status" aria-live="polite">
                {noShop && (
                  <p className="small access-system-warn">{t(scope.warning)}</p>
                )}
              </div>
            )}

            <div className="access-system-actions">
              <span className="muted small">
                {open
                  ? t("users.sectionsOn", {
                      on: String(granted),
                      total: String(system.rows.length),
                    })
                  : t("users.systemClosed")}
              </span>
            </div>
          </section>
        );
      })}
    </div>
  );
}
